import appNexusSync from './appNexus';
import { readCookie } from './cookie';
import { cookieConfig } from './cookie-config';
import { CisSyncReason, getSyncState, parsePulseCookies, serializePulseCookie } from './pulse-cookie';
const { PULSE_COOKIE_KEY } = cookieConfig;
const CIS_HOST = 'https://cis.schibsted.com';
const CIS_PATH = '/api/v2/identify';
let preferredCISHost = 'cis.schibsted.com';
/**
 * CIS has two endpoints:
 * - anonymous identifier keys (/identify/guest),
 * - identifiable keys (/identify/user)
 */
const CISActorType = {
    USER: 'user',
    GUEST: 'guest',
};
/**
 * Resolves CIS path param
 * @param actorId
 */
const getActorPath = (actorId) => {
    let endpoint = CISActorType.GUEST;
    if (actorId) {
        endpoint = CISActorType.USER;
    }
    return `${CIS_PATH}/${endpoint}`;
};
const cisFetchHeaders = new Headers({
    'Content-Type': 'application/json',
});
const CIS_MAX_REDIRECT_ATTEMPTS = 2;
const CIS_DEFAULT_REALM = 'schibsted.com';
const cisRequestMap = new Map();
/**
 * Call CIS with the current user information.
 *
 * @private
 */
async function cisFetch({ anId, cisLocation, clientId, includeAdvertising, resolvedJwe: jwe, trackerType, trackerVersion, userId, vendors, } = {}) {
    return fetch(cisLocation + getActorPath(userId), {
        method: 'POST',
        credentials: 'include',
        headers: cisFetchHeaders,
        body: JSON.stringify({
            anId,
            clientId,
            includeAdvertising,
            jwe,
            trackerType,
            trackerVersion,
            userId,
            vendors,
        }),
    });
}
/** Identify recursively until no `redirectToHost` field */
async function cisFetchWithRedirect({ anId, cisLocation, clientId, includeAdvertising, resolvedJwe, trackerType, trackerVersion, userId, vendors, }, redirectAttempts = 0) {
    if (redirectAttempts > CIS_MAX_REDIRECT_ATTEMPTS) {
        throw new Error('Too many CIS redirects');
    }
    const response = await cisFetch({
        anId,
        cisLocation,
        clientId,
        includeAdvertising,
        resolvedJwe,
        trackerType,
        trackerVersion,
        userId,
        vendors,
    });
    const { data: responseData } = await response.json();
    if (responseData.redirectToHost) {
        preferredCISHost = responseData.redirectToHost;
        return cisFetchWithRedirect({
            anId,
            resolvedJwe,
            userId,
            cisLocation: `https://${preferredCISHost}`,
            clientId,
            trackerType,
            trackerVersion,
            includeAdvertising,
            vendors,
        }, redirectAttempts + 1);
    }
    return responseData;
}
let currentCisRequest = Promise.resolve(parsePulseCookies());
let currentAppNexusRequest = Promise.resolve(parsePulseCookies());
/**
 * Call CIS serially if needed, 'needed' being evaluated when the call reaches the top of the queue.
 *
 * @private
 */
async function serialSyncWithCis(shortUserId, forceSync = false, cisLocation, nativeJwe, realm, reason, clientId, trackerType, trackerVersion, options) {
    // Re-read our sync state as the previous cis request might have
    // made this sync obsolete.
    const cookie = readCookie(PULSE_COOKIE_KEY);
    const { syncWithCis: shouldSyncWithCIS, syncWithCisReason: requestReason } = getSyncState(shortUserId, {
        cookie,
        investigateSyncOn: [reason],
        ...options,
    });
    const { anId, jwe, prevUserId } = parsePulseCookies(cookie);
    const requestHandlerFn = async () => {
        // For reason === login, we know shortUserId should be set and should be trusted.
        // For reason === logout, we know shortUserId is unset, but should be trusted.
        // For reason === initialise, we can't trust shortUserId and should use prevUserId instead.
        // For reason === refresh, we could use either as they should be in sync, but using
        // prevUserId makes it possible to do regular syncs without access to an event, so we use that.
        const resolvedJwe = nativeJwe || jwe; // always send a nativeJwe when present. Hybrid app use case;
        let prominentUserId = shortUserId;
        if (requestReason !== CisSyncReason.Login && requestReason !== CisSyncReason.Logout) {
            prominentUserId = prevUserId;
        }
        const userId = prominentUserId && `sdrn:${realm}:user:${prominentUserId}`;
        if (shouldSyncWithCIS || forceSync) {
            try {
                const cisResponseData = await cisFetchWithRedirect({
                    anId,
                    resolvedJwe,
                    userId,
                    cisLocation,
                    clientId,
                    trackerType,
                    trackerVersion,
                    includeAdvertising: options?.includeAdvertising,
                    vendors: options?.vendors,
                });
                const refreshAfterTimestamp = cisResponseData.refreshAfter
                    ? new Date(cisResponseData.refreshAfter).getTime()
                    : 0;
                const storageData = {
                    ppId: {},
                    ...cisResponseData,
                    prevUserId: shortUserId,
                    refreshAfterTimestamp,
                    includeAdvertising: options?.includeAdvertising,
                };
                serializePulseCookie(storageData);
                return storageData;
            }
            catch (err) {
                // silent error.
            }
            finally {
                if (options?.trackerId) {
                    cisRequestMap.delete(options.trackerId);
                }
            }
        }
        return parsePulseCookies();
    };
    if (options?.trackerId) {
        const { request, requestReason: onGoingRequestReason } = cisRequestMap.get(options.trackerId) || {};
        let ongoingRequest = request;
        if (!ongoingRequest || onGoingRequestReason !== requestReason || forceSync) {
            ongoingRequest = requestHandlerFn();
            cisRequestMap.set(options.trackerId, { request: ongoingRequest, requestReason });
        }
        return ongoingRequest;
    }
    // maintaining the old behaviour for when there is no "internalId" passed to the tracker.
    currentCisRequest = currentCisRequest.then(requestHandlerFn);
    return currentCisRequest;
}
/**
 * Sync with CIS
 *
 * This method deals with all communication and communication with dependent systems (currently AppNexus and Norstat).
 *
 * An optional user id should be provided if a tracker have received a logged in state. Providing a new user id
 * (different from a previous user id, serialized to the pulse cookie) will cause a "blocking call" to CIS (the
 * returned promise will not resolve before the communication is done). This is done because CIS could provide new data
 * regarding opt-out.
 *
 * The returned promise will also be blocking if no pulse cookie is found. Pulse requires environmentIds generated
 * server side so one have to wait to be allocated to one.
 *
 * In other context CIS could or could not be called but the returned promise will resolve immediately. If CIS have
 * been called recently there is no point in calling it again. In a different case, if a refresh call to CIS is required
 * it doesn't mean that other systems have to wait of it. Both cases will return a promise that resolves more or less
 * immediately.
 *
 * @returns {Promise<PulseCookie>} Events should be blocked until the returned promise
 * resolves.
 *
 */
export async function syncWithCis(shortUserId, nativeJwe, userServiceLocation, realm = CIS_DEFAULT_REALM, investigateSyncOn, xandrEnabled = true, clientId, trackerType, trackerVersion, options) {
    const { syncWithAppNexus: shouldSyncWithXandr, syncWithCis: shouldSyncWithCIS, waitForCis: shouldWaitForCIS, syncWithCisReason, } = getSyncState(shortUserId, { investigateSyncOn, xandrEnabled, ...options });
    const storageData = parsePulseCookies();
    const cisLocation = userServiceLocation || CIS_HOST;
    let cisRequest = Promise.resolve(storageData);
    if (shouldSyncWithCIS || options?.forceSync) {
        cisRequest = serialSyncWithCis(shortUserId, Boolean(options?.forceSync), cisLocation, nativeJwe, realm, syncWithCisReason, clientId || '', trackerType || '', trackerVersion || '', options);
    }
    // If `shouldWaitForCIS` is false the update with CIS is only a "soft sync" and
    // shouldn't block other operations. This is the case when the browser has a
    // _pulse2data cookie and the user haven't logged in or out. When the user do
    // log in we have to wait to avoid sending events with user ids after logging
    // out or without ids after logging in.
    let ongoingCISRequest = Promise.resolve(storageData);
    if (shouldWaitForCIS || options?.forceSync) {
        ongoingCISRequest = cisRequest;
    }
    if (xandrEnabled && shouldSyncWithXandr && options?.includeAdvertising) {
        // There is not a strict dependency between AppNexus and CIS here, but
        // AppNexus can send a 302 to CIS. We want to wait for the initial CIS
        // handshake to set all cookies on schibsted.com before letting AppNexus
        // redirect there (potentially setting new cookies)
        currentAppNexusRequest = currentAppNexusRequest
            .then(() => ongoingCISRequest) // Wait for any other AppNexus request
            .then(() => appNexusSync(cisLocation)) // Wait for current AppNexus request
            .then((shouldResyncWithCis) => {
            // Syncing with AppNexus could have resulted in a new anId that must be synchronized with CIS.
            if (shouldResyncWithCis) {
                return serialSyncWithCis(shortUserId, true, cisLocation, nativeJwe, realm, CisSyncReason.NewAnid, clientId || '', trackerType || '', trackerVersion || '', options);
            }
            return parsePulseCookies();
        });
    }
    return ongoingCISRequest;
}
//# sourceMappingURL=cis-browser.js.map